import React, { useEffect, useState } from "react"
import dayjs from "dayjs"
import { Modal } from "@mui/material"
import FirstStep from "./FirstStep"
import SecondStep from "./SecondStep"
import ThirdStep from "./ThirdStep"
import Button from "../Button/Button"
import { now, serverToLocal } from "../../utils/date"
import { useDispatch } from "react-redux"
import {
  calculatePaymentSchedule,
  getLatestCreditReportByUserId,
  getSavedDebtsByUser,
  saveDebtByUserId,
  savePaymentSchedule
} from "../../features/creditReports/creditReports"
import { getContactGroupCustomSettings } from "../../features/contactGroups/contactGroups"
import { getLeadDetails } from "../../features/contacts/contacts"
import { showSnackBar } from "../Snackbar/Snackbar"
import { restoreFeeShape, feeAmountError } from './feeShapePick'
import { saveDebtsAndPlan } from "./saveDebtsAndPlan"
import CustomStepper from "../Steper/Steper"
import { LuX } from "react-icons/lu"
import { isEmptyFields } from "../../helpers/helper"
import { getBankAccountsDropdown, getDashboardBalance } from "../../features/payment/paymentSlice"
import Spinner from "../Spiner/Spiner"
import { StorageGetItem, USER } from "../../lib/storage"
import { useIsSupervisor } from "../../hooks/user"

const steps = ['0', '1', '2']

const CreditReportModal = ({ userId, setIsUpdate, isUpdate, debts, dataPaymentsSchedule, flatFeeTyped = false, lowerFeeFloor = false }) => {
  const dispatch = useDispatch()
  const [selectedRows, setSelectedRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeStep, setActiveStep] = useState(0)
  const [viewStep, setViewStep] = useState(activeStep)
  const [data, setData] = useState([])
  const [debtsToDelete, setDebtsToDelete] = useState([])
  const [open, setOpen] = useState(false)
  const [bankUsersDropdown, setBankUsersDropdown] = useState(null)
  const [paymentScheduleData, setPaymentSchedule] = useState([])
  const [calculatedData, setCalculatedData] = useState({
    programLength: "",
    programFee: "",
    custodialFee: 0,
    cycleStartDate: now(),
    cycleSecondStartDate: now(),
    summonsFeeDate: now(),
    summonsFeePrice: "",
    billingAccountId: "",
    frequency: "Monthly",
    billingDates: "",
    initialFee: "",
    paymentDays: [],
    startFromMonth: null,
    // Percentage until the rep says otherwise — the same default the server applies. Held
    // explicitly so the shape is never undefined, which the server reads as a percentage pick.
    feeType: 'percent',
    flatFee: null,
  })
  const [loaded, setLoaded] = useState(false)
  const [heightModalContent, setHeightModalContent] = useState(460)
  const [user, setUser] = useState('')
  const [update, setUpdate] = useState(false)
  const [creditReport, setCreditReport] = useState({})
  const [dashboardData, setDashboardData] = useState({})
  const [disableInitialFee, setDisableInitialFee] = useState(false)
  // What the contact's own office permits for the program fee. Resolved by the API, which knows
  // the office; the screen only asks whether to offer the choice.
  const [feeShape, setFeeShape] = useState(null)

  const isSupervisor = useIsSupervisor()

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUser(result)
    })
  }, [])

  useEffect(() => {
    const loadGroupConfig = async () => {
      const stored = await StorageGetItem(USER)
      if (stored?.role !== 'affiliate') return
      const activeOfficeId = localStorage.getItem('activeOfficeId')
      const allGroups = stored?.contactGroups || []
      const groupIds = activeOfficeId ? [activeOfficeId] : (allGroups.length === 1 ? allGroups : [])
      if (groupIds.length === 0) return
      const settings = await Promise.all(
        groupIds.map(gid =>
          dispatch(getContactGroupCustomSettings({ groupId: gid }))
            .then(r => r?.payload?.data?.customSettings || null)
            .catch(() => null)
        )
      )
      if (settings.some(cs => cs?.disableInitialFee === true)) {
        setDisableInitialFee(true)
      }
    }
    loadGroupConfig()
  }, [])

  useEffect(() => {
    if (!userId) return
    dispatch(getLeadDetails(userId))
      .then(r => setFeeShape(r?.payload?.data?.contactGroupConfig?.feeShape || null))
      .catch(() => setFeeShape(null))
  }, [userId])

  // A flat-fee client never sets a percentage, so gating "Calculate" on programFee would leave the
  // button dead for them. The client's own shape decides this, not whether a separate request
  // happened to land: anding it with feeShape?.allowed meant a failed office-config call judged a
  // flat client on the percentage field they never set — blocking Calculate and dropping the pick
  // from the payload.
  const isFlatPick = calculatedData?.feeType === 'flat'

  // Whether a request should carry the fee pick at all: an office that permits a flat fee, OR a
  // client already on one. The second half is the important one. feeShape arrives from a separate
  // lead-details request, and gating only on it meant a blip on that call dropped the pick — which
  // the server reads as a deliberate percentage choice, silently re-pricing a signed client. The
  // server re-checks the office permission and refuses a flat pick it does not allow, so sending it
  // is always safe. On an ordinary percentage office neither half holds and the payload is
  // unchanged.
  const sendsFeeShape = !!feeShape?.allowed || isFlatPick

  const getUniqueId = () => {
    return Math.random().toString(36).substr(2, 9)
  }

  const getCreditReports = async () => {
    setLoaded(false)
    try {
      const response = await dispatch(getSavedDebtsByUser(userId))
      if (response.payload && Array.isArray(response.payload.data)) {
        const newData = response.payload.data.map(item => ({ ...item, id: getUniqueId() }))
        setData(newData)
      } else {
        console.error('No data received from server')
      }
    } catch (error) {
      console.error('Error fetching credit reports:', error)
    } finally {
      setLoading(false)
      setLoaded(true)
    }
  }

  const getCreditReportByUser = async () => {
    setLoaded(false)
    try {
      const response = await dispatch(getLatestCreditReportByUserId({ userId }))
      const data = response?.payload?.data
      if (data) {
        setCreditReport(data)
      } else {
        showSnackBar("error", "No credit report found for this user")
      }
    } catch (error) {
      console.error('Error fetching credit report:', error)
      showSnackBar("error", "Failed to load credit report")
    } finally {
      setLoading(false)
      setLoaded(true)
    }
  }

  const getDataBillingInfo = async () => {
    try {
      const bankUsersDropdownData = await dispatch(getBankAccountsDropdown(userId))
      const data = bankUsersDropdownData?.payload?.data
      setBankUsersDropdown(data)
      if (data?.bankAccounts?.length > 0) {
        setCalculatedData(prevData => ({ ...prevData, billingAccountId: data?.bankAccounts[0]?.value }))
      } else if (data?.creditCards?.length > 0) {
        setCalculatedData(prevData => ({ ...prevData, billingAccountId: data?.creditCards[0]?.value }))
      }

    } catch (error) {
      console.error('An unexpected error occurred:', error)
    }
  }

  const presetCalculatedData = async () => {
    const firstSchedule = dataPaymentsSchedule[0]

    setCalculatedData(prev => ({
      ...prev,
      programLength: firstSchedule?.programLength || prev.programLength,
      programFee: firstSchedule?.programFee ?? prev.programFee,
      custodialFee: firstSchedule?.custodialFee ?? prev.custodialFee,
      cycleStartDate: serverToLocal(dataPaymentsSchedule?.startDate) || now(),
      cycleSecondStartDate: serverToLocal(dataPaymentsSchedule?.secondStartDate) || now(),
      summonsFeeDate: now(),
      summonsFeePrice: firstSchedule?.summonsFeePrice || prev.summonsFeePrice,
      frequency: firstSchedule?.frequency || prev.frequency,
      billingDates: firstSchedule?.billingDates || prev.billingDates,
      initialFee: firstSchedule?.initialFee || prev.initialFee,
      paymentDays: firstSchedule?.paymentDays || [],
      startFromMonth: firstSchedule?.startFromMonth || null,
      // Without this a flat client re-opened as "Percentage" with no amount, and saving converted
      // them to a percentage of their debt — the exact drift a flat fee exists to prevent.
      ...restoreFeeShape(firstSchedule),
    }))
  }

  useEffect(() => {
    const fetch = async () => {
      setData([])
      await getDataBillingInfo()
      await getCreditReports()
      await getCreditReportByUser()
      await getDashboardData()
      await presetCalculatedData()
    }
    fetch()
  }, [update])

  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) return
    setSelectedRows(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev
      return prev.map(row => {
        const fresh = data.find(d => d?._id === row?._id)
        return fresh ? { ...row, ...fresh } : row
      })
    })
  }, [data])

  useEffect(() => {
    if (!open) return
    setUpdate(prev => !prev)
  }, [open, debts])

  useEffect(() => {
    if (Array.isArray(dataPaymentsSchedule) && dataPaymentsSchedule.length > 0) {
      presetCalculatedData()
    }
  }, [dataPaymentsSchedule])

  const calculatePayment = async () => {
    setLoaded(false)
    try {
      const data = {
        userId,
        programLength: calculatedData?.programLength,
        programFee: calculatedData?.programFee,
        custodialFee: calculatedData?.custodialFee,
        frequency: calculatedData?.frequency,
        cycleStartDate: calculatedData?.cycleStartDate,
        cycleSecondStartDate: calculatedData?.secondDate,
        billingAccountId: calculatedData?.billingAccountId,
        initialFee: calculatedData?.initialFee,
        paymentDays: calculatedData?.paymentDays,
        // Omitted unless a flat fee is in play, so an ordinary percentage office sends exactly what
        // it always did and the server falls back to the office shape. See sendsFeeShape.
        ...(sendsFeeShape ? { feeType: calculatedData?.feeType, flatFee: calculatedData?.flatFee } : {}),
        debts: selectedRows.map(item => ({
          _id: item._id,
          balanceAmount: item.balanceAmount,
        }))
      }
      const response = await dispatch(calculatePaymentSchedule(data))

      // rejected thunks resolve (no .unwrap()), so the catch below never sees API
      // failures — without this check a 400 silently rendered an empty schedule row
      if (response?.error) {
        showSnackBar("error", typeof response.payload === 'string'
          ? response.payload
          : "Failed to calculate the payment schedule")
        return
      }

      setPaymentSchedule([{ id: 1, ...response?.payload?.data }])
    } catch (error) {
      console.error('Error calculating payment:', error)
    } finally {
      setLoaded(true)
    }
  }

  const savePayment = async () => {
    try {
      setLoading(true)
      const response = await dispatch(savePaymentSchedule({
        data: {
          programLength: Number(calculatedData?.programLength),
          programFee: Number(calculatedData?.programFee),
          // Same pick as the calculation above. Saving under a different shape from the one the
          // operator was shown is the failure the server's matching resolution guards against.
          ...(sendsFeeShape ? { feeType: calculatedData?.feeType, flatFee: calculatedData?.flatFee } : {}),
          custodialFee: calculatedData?.custodialFee,
          frequency: calculatedData?.frequency,
          cycleStartDate: calculatedData?.cycleStartDate,
          cycleSecondStartDate: calculatedData?.secondDate,
          paymentDays: calculatedData?.paymentDays,
          startFromMonth: calculatedData?.startFromMonth,
          billingAccountId: calculatedData?.billingAccountId,
          totalPayment: paymentScheduleData[0]?.totalPayment,
          accountBalance: paymentScheduleData[0]?.accountBalance,
          totalDebts: paymentScheduleData[0]?.totalDebts,
          initialFee: paymentScheduleData[0]?.initialFee,
          debtsToDelete,
          debts: selectedRows.map(row => ({
            _id: row?._id,
            balanceAmount: row?.balanceAmount,
          })),
        },
        userId
      }))
      setIsUpdate(!isUpdate)
      return response
    } catch (error) {
      console.error('Error saving payment:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveDebt = async () => {
    try {
      const mappedSelectedRows = selectedRows.map(row => ({
        _id: row?._id,
        lastPaymentDate: row.lastPaymentDate,
        originalLoanAmount: row.originalLoanAmount,
        balanceAmount: row.balanceAmount,
        actualPaymentAmount: row.actualPaymentAmount,
        bankAccount: row.bankAccount,
        accountNumber: row.accountNumber,
        accountTypeId: row.accountType?.[0]?._id || row.accountType?._id,
      }))

      const response = await dispatch(saveDebtByUserId({
        data: mappedSelectedRows,
        userId
      }))
      return response
    } catch (error) {
      console.error('Error saving debts:', error)
    }
  }

  const handleStep = (step) => () => {
    setActiveStep(step)
  }
  const handleOpen = () => {
    setSelectedRows(debts?.filter(debt => debt.isDebt === true))
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setPaymentSchedule([])
    setCalculatedData(prev => getInitialCalculatedData(prev, dataPaymentsSchedule[0]))
    setActiveStep(0)
    setViewStep(0)
    setSelectedRows([])
    setDebtsToDelete([])
  }

  const handleCalculateSchedule = () => {
    // A flat client never sets a percentage, so judging them on it left the button clickable and
    // then refusing — which blocked the feature outright. Each shape is judged on its own field.
    const error = feeAmountError({
      isFlat: isFlatPick,
      programFee: calculatedData.programFee,
      flatFee: calculatedData.flatFee,
      typed: flatFeeTyped,
    })

    if (error) {
      showSnackBar("error", error)
    } else {
      setActiveStep(2)
      setViewStep(2)
      calculatePayment()
    }
  }

  const getInitialCalculatedData = (prev, data) => ({
    ...prev,
    ...(data?.programLength !== undefined && { programLength: data.programLength }),
    ...(data?.programFee !== undefined && { programFee: data.programFee }),
    ...(data?.custodialFee !== undefined && { custodialFee: data.custodialFee }),
    ...(data?.cycleStartDate && { cycleStartDate: dayjs(data.cycleStartDate) }),
    ...(data?.cycleSecondStartDate && { cycleSecondStartDate: dayjs(data.cycleSecondStartDate) }),
    ...(data?.summonsFeeDate && { summonsFeeDate: dayjs(data.summonsFeeDate) }),
    ...(data?.summonsFeePrice !== undefined && { summonsFeePrice: data.summonsFeePrice }),
    ...(data?.frequency && { frequency: data.frequency }),
    ...(data?.billingDates !== undefined && { billingDates: data.billingDates }),
    ...(data?.initialFee !== undefined && { initialFee: data.initialFee }),
    ...(data?.paymentDays !== undefined && { paymentDays: data.paymentDays }),
    ...(data?.startFromMonth !== undefined && { startFromMonth: data.startFromMonth }),
    ...restoreFeeShape(data),
  })

  let stepContent
  let modalButton


  switch (viewStep) {
    case 0:
      stepContent = <FirstStep data={data} setData={setData} setSelectedRows={setSelectedRows}
        selectedRows={selectedRows} loaded={loaded} userId={userId} update={update}
        setUpdate={setUpdate} debtsToDelete={debtsToDelete} setDebtsToDelete={setDebtsToDelete}
        setTriggerFetchDebts={setIsUpdate} isUpdate={isUpdate} creditReport={creditReport} />
      modalButton = <Button
        disabled={isEmptyFields(selectedRows)}
        onClick={() => {
          setActiveStep(1)
          setViewStep(1)
        }} width="min-w-[156px]">Next</Button>
      break
    case 1:
      stepContent =
        <SecondStep calculatedData={calculatedData} setCalculatedData={setCalculatedData}
          bankUsersDropdown={bankUsersDropdown} userId={userId}
          userRole={user?.role}
          disableInitialFee={disableInitialFee}
          feeShape={feeShape}
          flatFeeTyped={flatFeeTyped}
          lowerFeeFloor={lowerFeeFloor}
        />
      modalButton = <Button
        disabled={isEmptyFields(calculatedData.programLength) || isEmptyFields(isFlatPick ? calculatedData.flatFee : calculatedData.programFee) || isEmptyFields(calculatedData.billingAccountId) || isEmptyFields(selectedRows) ? isEmptyFields(calculatedData.summonsFeeDate) || isEmptyFields(calculatedData.summonsFeePrice) : false}
        onClick={() => handleCalculateSchedule()} width="min-w-[156px]">Calculate Payment Schedule</Button>
      break
    case 2:
      stepContent = <ThirdStep loading={loading} data={paymentScheduleData} />
      modalButton = <Button onClick={async () => {
        // The step icons reach Save without Calculate, so a typed flat amount is checked again here
        // rather than meeting the server's refusal as "Failed to save debts". Flat only: a percentage
        // plan's Save is left exactly as it was.
        if (flatFeeTyped && isFlatPick) {
          const feeError = feeAmountError({ isFlat: true, flatFee: calculatedData.flatFee, typed: true })
          if (feeError) {
            showSnackBar("error", feeError)
            return
          }
        }
        const { ok } = await saveDebtsAndPlan({ savePayment, saveDebt })
        if (ok) {
          showSnackBar("success", "Payment Schedule Is Successfully Saved")
          handleClose()
        } else {
          showSnackBar("error", "Failed to save debts. Please try again.")
        }
      }} width="min-w-[156px]">Save</Button>
      break
  }

  useEffect(() => {
    setCalculatedData(prev => getInitialCalculatedData(prev, dataPaymentsSchedule[0]))
    setActiveStep(0)
    setViewStep(0)
  }, [selectedRows])

  useEffect(() => {
    if (activeStep !== 0) {
      setActiveStep(1)
      setViewStep(1)
    }
    setPaymentSchedule([])
  }, [calculatedData])

  const getDashboardData = async () => {

    try {
      let result = await dispatch(getDashboardBalance(userId))
      if (result.payload && result.payload.data) {
        setDashboardData(result?.payload?.data)
      } else {
        console.error('No data received from server')
      }
    } catch (error) {
      console.error('An unexpected error occurred:', error)
    }
  }

  return (
    <div>
      {(user?.role === 'customer' || isSupervisor) &&
        <Button onClick={handleOpen} type="outlined" classes=" !border-primary-main">
          <p className={`flex `}>Change debts and regenerate plan</p>
        </Button>}
      <Modal
        open={open}
        closeAfterTransition
        onClose={handleClose}
      >
        <div className="flex items-center justify-center h-screen ">
          <div
            className={`bg-surface rounded-2xl flex flex-col justify-between min-w-[720px] max-h-content `}>
            <div className="pl-6 pr-6 pt-6">
              <div className="mb-3 flex justify-between">
                <h4 className="font-sans text-lg-18 text-text-primary">{activeStep === 0 ? "Debt Program" : "Program Fees"}</h4>
                <div className="inline-flex justify-center items-center rounded-full cursor-pointer text-text-secondary"
                  onClick={handleClose}>
                  <LuX size={20} />
                </div>
              </div>
              {activeStep === 1 && <div className="text-md flex flex-row items-center mb-3">
                <span>Already Paid: </span>
                <span className="mx-3 text-success-main">${dashboardData?.amount?.paid || 0}</span>
              </div>}
            </div>
            <div className="flex-shrink-0 w-full h-px bg-divider" />
            <div className="pl-6 pr-6 pb-6">
              <div className="py-6 ">
                <div className="w-full">
                  <CustomStepper activeStep={activeStep} viewStep={viewStep} steps={steps} handleStep={handleStep}
                    setActiveStep={setActiveStep} setViewStep={setViewStep} />
                </div>
                <div className="py-6 overflow-auto" style={{ maxHeight: heightModalContent }}>{stepContent}</div>
              </div>
            </div>
            <div className="flex-shrink-0 w-full h-px bg-divider" />
            <div className={`mt-6 mr-6 mb-6 flex justify-end space-x-4`}>
              <Button onClick={handleClose} type="outlined" size="lg" width="w-[156px]">Close</Button>
              {modalButton}
            </div>
          </div>
        </div>
      </Modal>
      {loading && <Spinner />}
    </div>
  )
}

export default CreditReportModal
